import { FC, useState, createContext, useContext } from "react";
import { useDeepCompareCallback } from "use-deep-compare";
import * as _ from "lodash";

import { Charts, DataDescriptorEntity, DataDictionary } from "@ctra/api";

import { useFilter, useISODuration, useTimePeriod } from "@chart";
import { useDataDictionary, useUserPreferences } from "@base";

import { ContextType, DateMethod, FilterMetadata, FilterProviderProps, SeriesDefinition } from "./typings";

/**
 * Default filter metadata
 * @type {{supportedISODurations: readonly [string, string, string, string, string, string, string, string, string, string], filterDefinitions: any[], flags: {isBackendAggregated: boolean}, offset: number, supportedfarmIDs: any[]}}
 */
const defaultMetadata: FilterMetadata = {
  supportedISODurations: Charts.utils.supportedDurations,
  filterDefinitions: [],
  flags: { isBackendAggregated: false },
  offset: 86400,
  supportedfarmIDs: []
};

const defaultSeries: SeriesDefinition = {
  available: {},
  active: []
};

/**
 * Default filter context value
 * @type {{metadata: FilterMetadata, series: any[], timePeriod: {}, filters: {}, api: {setMetadata: (...args: any[]) => void, setISODuration: (...args: any[]) => void, reset: (...args: any[]) => void, setCurrentDateMethod: (...args: any[]) => void, setTimePeriod: (...args: any[]) => void, setFilter: (...args: any[]) => void}, isoDuration: null}}
 */
export const defaultFilterContextValue: ContextType = {
  filters: {},
  timePeriod: {},
  isoDuration: null,
  bucketSize: DataDictionary.DefaultDataInterval,
  api: {
    reset: _.noop,
    getSeries: (variantID?: DataDescriptorEntity["id"]) => defaultSeries,
    setSeries: _.noop,
    setISODuration: _.noop,
    setBucketSize: _.noop,
    setFilter: _.noop,
    setTimePeriod: _.noop,
    getMetadata: (variantID?: DataDescriptorEntity["id"]) => defaultMetadata,
    setMetadata: _.noop,
    setCurrentDateMethod: _.noop
  }
};

const DefaultContext = createContext<ContextType>(defaultFilterContextValue);

/**
 * Provides API for changing time, unit, series, and filters
 * @note values come from props, section/chart that call the setter APIs down the components tree
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @param {{}} initialFilters
 * @param {any[]} initialSeries
 * @param {string | null | undefined} initialISODuration
 * @param {{}} initialTimePeriod
 * @param {string} initialBucketSize
 * @param {{isoDuration?: Nullable<ISODuration>, timePeriod?: ChartTimePeriod, bucketSize?: ISODuration} | undefined} currentValues
 * @param {ReadonlyArray<ISODuration> | undefined} durationOptions
 * @returns {JSX.Element}
 */
const _ChartFilterProvider: FC<FilterProviderProps> = ({
  children,
  filters: initialFilters = {},
  series: initialSeries = {},
  isoDuration: initialISODuration,
  timePeriod: initialTimePeriod = {},
  bucketSize: initialBucketSize = DataDictionary.DefaultDataInterval,
  currentValues,
  durationOptions
}) => {
  const { dataDescriptors } = useDataDictionary();

  const {
    preferences: {
      sandbox: { isEnabled: isSandboxEnabled }
    }
  } = useUserPreferences();

  /**
   * Manage the time period and the time unit via ISO durations
   */
  const [isoDuration, setISODuration] = useISODuration(initialISODuration);
  const [bucketSize, setBucketSize] = useISODuration(initialBucketSize);

  /**
   * Manage the active and available series (visual filter to be saved)
   */
  const [seriesState, setSeriesState] = useState<Record<DataDescriptorEntity["id"], SeriesDefinition>>(
    _.mapValues(initialSeries, (series) => ({
      available: {},
      active: series
    }))
  );

  /**
   * Get series for the given variant ID
   * @type {(variantID: DataDescriptorEntity["id"]) => any}
   */
  const getSeries = useDeepCompareCallback(
    (variantID?: DataDescriptorEntity["id"]) => {
      return variantID ? _.get(seriesState, [variantID], defaultSeries) : defaultSeries;
    },
    [seriesState]
  );

  /**
   * Set seried for the given variant ID
   * @param {DataDescriptorEntity["id"]} variantID
   * @param {SeriesDefinition} series
   */
  const setSeries = (variantID: DataDescriptorEntity["id"], series?: SeriesDefinition) => {
    setSeriesState((prevSeries) => ({
      ...prevSeries,
      [variantID]: _.defaultTo(series, defaultSeries)
    }));
  };

  /**
   * Manage the chart metadata
   */
  const [metadataState, setMetadataState] = useState<Record<DataDescriptorEntity["id"], FilterMetadata>>({});

  /**
   * Get filter metadata for the given variant ID
   * @type {(variantID?: DataDescriptorEntity["id"]) => any}
   */
  const getFilterMetadata = useDeepCompareCallback(
    (variantID?: DataDescriptorEntity["id"]) => {
      const variant = variantID ? dataDescriptors[variantID] : null;

      const defaultValues = _.merge(
        {},
        defaultMetadata,
        durationOptions
          ? {
              supportedISODurations: durationOptions
            }
          : {},
        variant
          ? {
              // keep only the filters that are backend aggregated - the rest are basically series filters
              filterDefinitions: _.filter(_.values(variant.filters), "isBackendAggregated")
            }
          : {}
      );

      return variantID ? _.get(metadataState, [variantID], defaultValues) : defaultValues;
    },
    [metadataState, durationOptions]
  );

  /**
   * Set filter metadata for the given variant ID
   * @param {DataDescriptorEntity["id"]} variantID
   * @param {FilterMetadata} metadata
   */
  const setFilterMetadata = (variantID: DataDescriptorEntity["id"], metadata?: FilterMetadata) => {
    const variant = variantID ? dataDescriptors[variantID] : null;

    const defaultValues = _.merge(
      {},
      defaultMetadata,
      durationOptions
        ? {
            supportedISODurations: durationOptions
          }
        : {},
      variant
        ? {
            // keep only the filters that are backend aggregated - the rest are basically series filters
            filterDefinitions: _.filter(_.values(variant.filters), "isBackendAggregated")
          }
        : {}
    );

    setMetadataState((prevMetadata) => ({
      ...prevMetadata,
      [variantID]: _.defaultTo(metadata, defaultValues)
    }));
  };

  /**
   * Tell whether the date used for data fetching is an ISO duration or a from-to time period
   * @type {DateMethod.iso}
   */
  const dateMethod = initialISODuration ? DateMethod.iso : DateMethod.custom;
  const [currentDateMethod, setCurrentDateMethod] = useState<DateMethod>(dateMethod);

  /**
   * Manage the from-to time period
   */
  const [timePeriod, setTimePeriod] = useTimePeriod(
    {
      isoDuration: currentValues?.isoDuration || isoDuration,
      timePeriod: currentValues?.timePeriod || initialTimePeriod,
      currentDateMethod: dateMethod
    },
    // pick the biggest offset in the metadata state
    _.defaultTo(_.max(_.map(metadataState, "offset")), 86400)
  );

  /**
   * Manage any eventual data filters (non visual, changes chart query and data fetching params)
   */
  const [filters, setFilter, reset] = useFilter(
    _.mapValues(metadataState, ({ filterDefinitions }) => Charts.utils.getDefaultFilters(filterDefinitions)),
    initialFilters
  );

  /**
   * Make a collection of filters
   * @type {{[p: string]: Pick<ChartFilters, never>}}
   */
  const filterCollection = isSandboxEnabled
    ? _.mapValues(filters, (filter) => _.omit(filter, "dimGroupKey"))
    : filters;

  return (
    <DefaultContext.Provider
      value={{
        filters: filterCollection,
        timePeriod,
        currentDateMethod,
        bucketSize: _.defaultTo(currentValues?.bucketSize, bucketSize),
        isoDuration: _.defaultTo(currentValues?.isoDuration, isoDuration),
        api: {
          reset,
          getSeries,
          setSeries,
          setFilter,
          setISODuration,
          setTimePeriod,
          setBucketSize,
          getMetadata: getFilterMetadata,
          setMetadata: setFilterMetadata,
          setCurrentDateMethod
        }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const ChartFilterContext = {
  Provider: _ChartFilterProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for Base Filter context
 * @returns
 */
export const useChartFilters = (): ContextType => useContext(DefaultContext);
